import { useState, type FormEvent } from 'react'
import * as stylex from '@stylexjs/stylex'

import type { CreatePersonInput, Person } from '../lib/ledger'
import AvatarEditor from './AvatarEditor'
import Dialog from './Dialog'
import Icon from './Icon'

const styles = stylex.create({
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  field: { display: 'block' },
  label: { marginBottom: '8px', display: 'block', fontSize: '13px', fontWeight: 600, color: '#3d463f' },
  input: {
    height: '44px',
    width: '100%',
    borderRadius: '0.75rem',
    borderWidth: 1,
    borderColor: '#dbe2d9',
    backgroundColor: '#fff',
    paddingInline: '12px',
    fontSize: '13px',
    outline: 'none',
    ':focus': { borderColor: '#5b9a6c', boxShadow: '0 0 0 3px color-mix(in srgb, #5b9a6c 10%, transparent)' },
  },
  textarea: {
    minHeight: '92px',
    width: '100%',
    resize: 'vertical',
    borderRadius: '0.75rem',
    borderWidth: 1,
    borderColor: '#dbe2d9',
    backgroundColor: '#fff',
    paddingInline: '12px',
    paddingBlock: '10px',
    fontSize: '13px',
    lineHeight: 1.5,
    outline: 'none',
    ':focus': { borderColor: '#5b9a6c', boxShadow: '0 0 0 3px color-mix(in srgb, #5b9a6c 10%, transparent)' },
  },
  grid: { display: 'grid', gap: '16px', '@media (min-width: 640px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } },
  help: { marginTop: '6px', fontSize: '11px', lineHeight: 1.45, color: '#829087' },
  error: { borderRadius: '0.75rem', backgroundColor: '#fff3ed', paddingInline: '12px', paddingBlock: '8px', fontSize: '12px', color: '#9b491e' },
  actions: { display: 'flex', justifyContent: 'end', gap: '12px', borderTopWidth: 1, borderColor: '#e8ece7', paddingTop: '20px' },
  cancel: { height: '40px', borderRadius: '0.75rem', paddingInline: '16px', fontSize: '13px' },
  save: { display: 'flex', height: '40px', alignItems: 'center', gap: '8px', borderRadius: '0.75rem', backgroundColor: '#408b56', paddingInline: '20px', fontSize: '13px', fontWeight: 600, color: '#fff', ':disabled': { cursor: 'not-allowed', opacity: .55 } },
  icon: { height: '16px', width: '16px' },
})

type Props = {
  person: Person
  onClose: () => void
  onSave: (input: CreatePersonInput) => Promise<void>
}

export default function EditPersonDialog({ person, onClose, onSave }: Props) {
  const [name, setName] = useState(person.name)
  const [email, setEmail] = useState(person.email ?? '')
  const [phone, setPhone] = useState(person.phone ?? '')
  const [avatar, setAvatar] = useState(person.avatar ?? '')
  const [notes, setNotes] = useState(person.notes ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSave({
        displayName: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        avatar,
        notes: notes.trim(),
      })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not update this person.')
      setSaving(false)
    }
  }

  return (
    <Dialog description="Keep this person's details, private notes, and avatar up to date." onClose={saving ? () => undefined : onClose} size="lg" title="Person details">
      <form {...stylex.props(styles.form)} onSubmit={submit}>
        <AvatarEditor key={`${person.id}:${name}:${avatar}`} name={name || person.name} onChange={setAvatar} value={avatar} />

        <label {...stylex.props(styles.field)}>
          <span {...stylex.props(styles.label)}>Full name</span>
          <input autoFocus {...stylex.props(styles.input)} onChange={(event) => setName(event.target.value)} value={name} />
        </label>

        <div {...stylex.props(styles.grid)}>
          <label {...stylex.props(styles.field)}>
            <span {...stylex.props(styles.label)}>Email</span>
            <input {...stylex.props(styles.input)} onChange={(event) => setEmail(event.target.value)} type="email" value={email} />
          </label>
          <label {...stylex.props(styles.field)}>
            <span {...stylex.props(styles.label)}>Phone</span>
            <input {...stylex.props(styles.input)} onChange={(event) => setPhone(event.target.value)} value={phone} />
          </label>
        </div>

        <label {...stylex.props(styles.field)}>
          <span {...stylex.props(styles.label)}>Private notes</span>
          <textarea {...stylex.props(styles.textarea)} onChange={(event) => setNotes(event.target.value)} placeholder="For example: Usually pays at the end of the month." value={notes} />
          <span {...stylex.props(styles.help)}>Only stored with this person in your SettleUp data.</span>
        </label>

        {error && <p {...stylex.props(styles.error)} role="alert">{error}</p>}
        <div {...stylex.props(styles.actions)}>
          <button {...stylex.props(styles.cancel)} disabled={saving} onClick={onClose} type="button">Cancel</button>
          <button {...stylex.props(styles.save)} disabled={saving} type="submit">
            {saving ? 'Saving…' : 'Save changes'}
            <Icon {...stylex.props(styles.icon)} name="check" />
          </button>
        </div>
      </form>
    </Dialog>
  )
}
